import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getAllDeployments,
    deleteDeployment,
    Deployment,
} from '../api/environmentApi';
import {
    RectangleGroupIcon,
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    ServerStackIcon,
    CloudIcon,
    CheckBadgeIcon,
} from '@heroicons/react/24/outline';

const ENV_COLORS: Record<string, string> = {
    prod:       'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    production: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    staging:    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    uat:        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    dev:        'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    development:'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    local:      'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};
const envColor = (name: string) => ENV_COLORS[name.toLowerCase()] ?? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';

const typeIcon = (type: string) => {
    if (type === 'azure')      return <CloudIcon className="h-4 w-4 text-blue-400" />;
    if (type === 'kubernetes') return <ServerStackIcon className="h-4 w-4 text-indigo-400" />;
    return <ServerStackIcon className="h-4 w-4 text-gray-400" />;
};

const EnvironmentsPage: React.FC = () => {
    const navigate = useNavigate();
    const [deployments, setDeployments] = useState<Deployment[]>([]);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState<string | null>(null);
    const [deleting, setDeleting]       = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await getAllDeployments();
            setDeployments(r.data ?? []);
        } catch (e: any) {
            setError(e?.message ?? 'Failed to load deployments');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleDelete = async (id: string, name: string) => {
        if (!window.confirm(`Delete deployment "${name}"? This cannot be undone.`)) return;
        setDeleting(id);
        try {
            await deleteDeployment(id);
            setDeployments(prev => prev.filter(d => d._id !== id));
        } catch (e: any) {
            alert(e?.message ?? 'Delete failed');
        } finally {
            setDeleting(null);
        }
    };

    return (
        <div className="px-6 py-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <RectangleGroupIcon className="h-6 w-6 text-gray-500" />
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Environments</h1>
                </div>
                <button
                    onClick={() => navigate('/environments/create')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                >
                    <PlusIcon className="h-4 w-4" />
                    New Deployment
                </button>
            </div>

            {error && (
                <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">{error}</div>
            )}

            {loading ? (
                <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 text-sm">
                    <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-primary-500 rounded-full" />
                    Loading…
                </div>
            ) : deployments.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-12 text-center">
                    <RectangleGroupIcon className="h-10 w-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No deployments yet</p>
                    <p className="text-xs text-gray-400 dark:text-gray-600 mt-1 mb-4">
                        Create a deployment to define your infrastructure environments and their Keycloak, Vault, and cluster configs.
                    </p>
                    <button
                        onClick={() => navigate('/environments/create')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                    >
                        <PlusIcon className="h-4 w-4" /> Create first deployment
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {deployments.map(dep => (
                        <div key={dep._id}
                            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                            {/* Deployment header */}
                            <div className="px-5 py-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {typeIcon(dep.type)}
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-900 dark:text-white">{dep.name}</span>
                                            {dep.isDefault && (
                                                <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                                                    <CheckBadgeIcon className="h-3 w-3" /> default
                                                </span>
                                            )}
                                            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">{dep.type}</span>
                                        </div>
                                        {dep.description && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{dep.description}</p>
                                        )}
                                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-400 dark:text-gray-500">
                                            {dep.acr?.loginServer   && <span>ACR: {dep.acr.loginServer}</span>}
                                            {dep.vault?.url         && <span>Vault: {dep.vault.url}</span>}
                                            {dep.domainBase         && <span>Domain: {dep.domainBase}</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => navigate(`/environments/${dep._id}`)}
                                        className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        title="Edit deployment"
                                    >
                                        <PencilSquareIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(dep._id!, dep.name)}
                                        disabled={deleting === dep._id}
                                        className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                                        title="Delete deployment"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Environments grid */}
                            {dep.environments.length > 0 ? (
                                <div className="border-t border-gray-100 dark:border-gray-700 px-5 py-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                                        Environments ({dep.environments.length})
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {dep.environments.map(env => (
                                            <div key={env.name}
                                                className="flex flex-col gap-0.5 px-3 py-2 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 min-w-[180px]">
                                                <span className={`self-start text-xs font-semibold px-1.5 py-0.5 rounded ${envColor(env.name)}`}>{env.name}</span>
                                                {env.keycloak?.baseUrl && (
                                                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate" title={env.keycloak.baseUrl}>
                                                        KC: {env.keycloak.baseUrl.replace(/^https?:\/\//, '')}
                                                    </span>
                                                )}
                                                {env.vault?.url && (
                                                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate" title={env.vault.url}>
                                                        Vault: {env.vault.url.replace(/^https?:\/\//, '')}
                                                    </span>
                                                )}
                                                {env.clusters && env.clusters.length > 0 && (
                                                    <span className="text-xs text-gray-400 dark:text-gray-500">
                                                        {env.clusters.length} cluster{env.clusters.length !== 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="border-t border-gray-100 dark:border-gray-700 px-5 py-3 text-xs text-gray-400 dark:text-gray-600">
                                    No environments — <button onClick={() => navigate(`/environments/${dep._id}`)} className="underline hover:text-gray-600">add one</button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default EnvironmentsPage;
